import { useEffect } from 'react';

import {
  MATERIAL_TYPE,
  SCREEN_ORIENTATION,
  SUBSECTION_TYPE,
} from 'Components/Classroom/Content/constant';
import { useVideoPlayer } from 'Context/VideoPlayer';

/*
  SKDAPP-111: This allows Flutter to communicate with Classroom's video player. 
    - Go fullscreen when the device is rotated.
    - Exit fullscreen when the device is rotated back to portrait.
    - Prevents the device from playing fullscreen in portrait by forcing the device into landscape mode if the video is in fullscreen.
*/

const FlutterVideoEventHandler = () => {
  const { player } = useVideoPlayer();

  // Handles device rotation
  const handleFlutterMessage = (event) => {
    const message = JSON.parse(event.data);
    /*
    SLIDE is the priority when handling fullscreen on rotate.
    - If the current subsection contains SLIDE, activate fullscreen slide instead of VIDEO.
    */
    if (
      message.subsectionType !== SUBSECTION_TYPE.VIDEO_SLIDE &&
      message.orientation === SCREEN_ORIENTATION.LANDSCAPE &&
      !player.isFullscreen() &&
      typeof player.requestFullscreen === 'function'
    ) {
      player.requestFullscreen();
      return;
    }

    // Resets the view when rotated back to portrait.
    const currentFullscreenContent = message.currentFullscreenContent;
    if (
      message.orientation === SCREEN_ORIENTATION.PORTRAIT &&
      // Prevent calling exitFullscreen() when the `SLIDES` is in fullscreen
      (currentFullscreenContent === MATERIAL_TYPE.VIDEO || currentFullscreenContent === 'NONE')
    ) {
      const isPaused = player.paused();
      // unfortunately, checking isFullscreen() here won't work with YouTube
      player.exitFullscreen();
      setTimeout(() => {
        // Prevents the video from stopping if it was initially playing
        if (!isPaused) player.play();
      }, 1000);
      return;
    }
  };

  useEffect(() => {
    if (player && typeof player.on === 'function') {
      // Receive message from Flutter
      window.addEventListener('message', handleFlutterMessage);

      // Notify Flutter on fullscreen change
      player.on('fullscreenchange', () => {
        const isFullscreen = player.isFullscreen();
        // This tells Flutter whether the video is being played in full screen
        window.flutter_inappwebview?.callHandler('handleFullscreenChange', {
          isFullscreen: isFullscreen,
          currentFullscreenContent: isFullscreen ? MATERIAL_TYPE.VIDEO : 'NONE',
        });

        /* ! ISSUE:
            When exiting full screen in iOS, the video will be paused.
            It is probably fixed in video.js version 7.8.4
            source: https://github.com/videojs/video.js/issues/5325#issuecomment-655857758
         */

        /* Current Workaround:
           Forces video play after fullscreen change, as it is a more common use case.
           For example,
            - Pressing full screen icon to watch the video in full screen.
            - While being in full screen, minimize the video to be able to simultaneously read the slides.
        */
        if (player.paused()) player.play();
      });
    }

    return () => window.removeEventListener('message', handleFlutterMessage);
  }, [player]);

  return null;
};

export default FlutterVideoEventHandler;
